import React, { useState } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { FeedUser } from '../api';
import './UserCard.css';

interface UserCardProps {
  user: FeedUser;
  rating: number | null;
  onCancel: () => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, rating, onCancel }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [dragY, setDragY] = useState(0);

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;
    
    if (info.offset.y < -threshold) {
      // Swipe down to cancel
      onCancel();
    }
    
    setDragY(0);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev < user.photos.length - 1 ? prev + 1 : prev
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  return (
    <motion.div
      className="user-card"
      drag="y"
      dragConstraints={{ top: -100, bottom: 0 }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      onDrag={(_event, info) => setDragY(info.offset.y)}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="card-header">
        <span className="user-name">{user.name}</span>
        <div className="photo-indicators">
          {user.photos.map((_, index) => (
            <div
              key={index}
              className={`indicator ${index === currentPhotoIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>

      <div className="photo-container">
        <img
          src={user.photos[currentPhotoIndex].url}
          alt={`${user.name}, ${currentPhotoIndex + 1} of ${user.photos.length}`}
          className="user-photo"
        />

        {currentPhotoIndex > 0 && (
          <button className="photo-nav-btn prev" onClick={prevPhoto}>
            ‹
          </button>
        )}

        {currentPhotoIndex < user.photos.length - 1 && (
          <button className="photo-nav-btn next" onClick={nextPhoto}>
            ›
          </button>
        )}

        {rating !== null && (
          <motion.div
            className="rating-overlay"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="rating-number">{rating}</div>
          </motion.div>
        )}

        {dragY < -20 && (
          <div className="cancel-hint">
            <p>↑ Release to cancel</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default UserCard;
